import type {
  AgentResearchJob,
  AgentResearchJobReadResponse,
  AgentResearchJobStatus,
  AgentResearchStopReason,
  GraphVisualizationFrame,
  PersonalAgentDevice,
  PersonalAgentPresenceHint,
  PrivateAgentProfile,
  PrivateAgentPlacement,
  PrivateAgentReadiness
} from "@salesko/contracts";
import { AgentAvatar, Badge, Banner, Button, Card, EmptyState, Spinner, StatusDot } from "@salesko/design-system";
import { useQuery } from "@tanstack/react-query";
import { Bot, Monitor } from "lucide-react";
import type { BadgeProps } from "@salesko/design-system";
import { useState, type CSSProperties } from "react";
import { useI18n, type Dictionary } from "../../i18n";
import { AgentResearchApiError, getAgentResearchJob } from "../../lib/api";
import { canonicalEntityIdForGraphNode } from "../lib/canonicalIdentity";
import { compactDateTime } from "../lib/format";
import { agentResearchJobIsCancellable, agentResearchJobIsTerminal } from "../hooks/usePrivateAgentJobs";
import { computerStatus } from "../settings/ComputersPanel";

/**
 * How reachable the computer bound to the Private Agent is right now.
 * `missing` covers both "no Private Agent yet" and "the bound computer is not
 * in the device list any more" — in neither case does a computer exist to be
 * online or offline, and neither is inferred from job state.
 */
export type PrivateAgentComputerAvailability = "online" | "offline" | "revoked" | "missing";

/**
 * Surface-specific copy and identifier visibility. The narrow detail panel
 * keeps its historical defaults, while page-level workspace consumers must
 * opt into user-facing fallback copy and identifier hiding explicitly.
 */
export type PrivateAgentJobsPresentationPolicy = {
  showJobId: boolean;
  targetFallbackLabel?: string;
};

const listStyle: CSSProperties = { display: "grid", gap: "10px" };
const rowHeadStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  flexWrap: "wrap"
};
const rowMetaStyle: CSSProperties = {
  margin: 0,
  color: "var(--text-muted)",
  fontFamily: "var(--font-sans)",
  fontSize: "var(--text-caption)"
};
const rowActionsStyle: CSSProperties = { display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" };
const headerIdentityStyle: CSSProperties = { display: "flex", alignItems: "center", gap: "10px" };

/**
 * Composes the Private Agent profile with the one device authority
 * (`GET /api/agent/devices`) and its TTL presence hint. The web never invents a
 * device state: if the bound computer is not in the list it is `missing`, not
 * "offline".
 */
export function privateAgentComputerAvailability(
  placement: PrivateAgentPlacement | null,
  devices: PersonalAgentDevice[],
  presenceHints: PersonalAgentPresenceHint[]
): PrivateAgentComputerAvailability {
  if (!placement) {
    return "missing";
  }
  const device = devices.find((candidate) => candidate.deviceId === placement.deviceId);
  if (!device) {
    return "missing";
  }
  return computerStatus(
    device,
    presenceHints.find((hint) => hint.deviceId === placement.deviceId)
  );
}

/**
 * The status dot's label, read off the server's readiness verdict. This is a
 * projection of a decision the API already made; it never re-computes
 * admission, which is `readiness.chat` alone.
 */
export function privateAgentReadinessComputerAvailability(
  readiness: PrivateAgentReadiness
): PrivateAgentComputerAvailability {
  if (readiness.chat === "ready") {
    return "online";
  }
  if (readiness.binding === "profile_missing") {
    return "missing";
  }
  if (readiness.binding === "revoked" || readiness.binding === "missing") {
    return "revoked";
  }
  return "offline";
}

/**
 * The job carries a canonical entity id, which is a deterministic function of a
 * node's type and label. Matching it back against the loaded frame gives the
 * name the owner recognises; with no match the id is shown as-is rather than
 * guessing a label.
 */
export function agentResearchTargetLabel(
  frame: GraphVisualizationFrame | null,
  targetCanonicalEntityId: string,
  fallbackLabel = targetCanonicalEntityId
): string {
  const match = frame?.graph.nodes.find(
    (node) => canonicalEntityIdForGraphNode(node) === targetCanonicalEntityId
  );
  return match?.label ?? fallbackLabel;
}

export function agentResearchJobStatusLabel(status: AgentResearchJobStatus, t: Dictionary): string {
  switch (status) {
    case "queued":
      return t.privateAgentJobs.status.queued;
    case "dispatching":
      return t.privateAgentJobs.status.dispatching;
    case "running":
      return t.privateAgentJobs.status.running;
    case "cancel_requested":
      return t.privateAgentJobs.status.cancelRequested;
    case "succeeded":
      return t.privateAgentJobs.status.succeeded;
    case "failed":
      return t.privateAgentJobs.status.failed;
    case "canceled":
      return t.privateAgentJobs.status.canceled;
    case "waiting_device":
      return t.privateAgentJobs.status.waitingDevice;
    case "erased":
      return t.privateAgentJobs.status.erased;
  }
}

/**
 * Colour is never the only signal — the Badge always carries the status word.
 * The tone only groups statuses: in-flight, needs-your-attention, done, gone.
 */
export function agentResearchJobStatusTone(status: AgentResearchJobStatus): NonNullable<BadgeProps["tone"]> {
  switch (status) {
    case "running":
    case "dispatching":
      return "person";
    case "succeeded":
      return "company";
    case "failed":
    case "waiting_device":
      return "event";
    case "canceled":
    case "cancel_requested":
    case "erased":
      return "note";
    case "queued":
      return "neutral";
  }
}

/**
 * The one place a job status and the computer's reachability are combined.
 * `waiting_device` is an API-written terminal status, so its copy is fixed;
 * `queued` is the only status whose reading depends on the computer, and the
 * three unreachable readings are kept apart instead of collapsed into
 * "offline".
 */
export function agentResearchJobStatusDetail(
  status: AgentResearchJobStatus,
  availability: PrivateAgentComputerAvailability,
  t: Dictionary
): string | null {
  if (status === "waiting_device") {
    return t.privateAgentJobs.statusDetail.waitingDevice;
  }
  if (status !== "queued") {
    return null;
  }
  if (availability === "offline") {
    return t.privateAgentJobs.statusDetail.queuedComputerOffline;
  }
  if (availability === "revoked" || availability === "missing") {
    return t.privateAgentJobs.statusDetail.queuedNoComputer;
  }
  return null;
}

export function agentResearchStopReasonLabel(reason: AgentResearchStopReason, t: Dictionary): string {
  switch (reason) {
    case "completed":
      return t.privateAgentJobs.stopReasons.completed;
    case "step_budget":
      return t.privateAgentJobs.stopReasons.stepBudget;
    case "time_budget":
      return t.privateAgentJobs.stopReasons.timeBudget;
    case "token_budget":
      return t.privateAgentJobs.stopReasons.tokenBudget;
    case "provider_limit":
      return t.privateAgentJobs.stopReasons.providerLimit;
  }
}

/** 409 from materialize is a specific, actionable state; anything else is generic. */
export function materializeErrorMessage(error: unknown, t: Dictionary): string {
  if (error instanceof AgentResearchApiError && error.code === "agent_research_frame_version_conflict") {
    return t.privateAgentJobs.frameConflict;
  }
  if (error instanceof AgentResearchApiError && error.code === "agent_research_result_not_ready") {
    return t.privateAgentJobs.resultNotReady;
  }
  return t.privateAgentJobs.materializeError;
}

export type PrivateAgentJobsPanelViewProps = {
  cancelingJobId: string | null;
  computerAvailability: PrivateAgentComputerAvailability;
  enabled: boolean;
  emptyState?: {
    description: string;
    title: string;
  };
  frame: GraphVisualizationFrame | null;
  jobs: AgentResearchJob[];
  jobsError: unknown;
  jobsLoading: boolean;
  materializeError: unknown;
  materializingJobId: string | null;
  onCancel: (jobId: string) => void;
  onMaterialize: (jobId: string) => void;
  onOpenComputers: () => void;
  onOpenPrivateAgentSettings: () => void;
  onSelectJob: (jobId: string | null) => void;
  profile: PrivateAgentProfile | null;
  profileLoading: boolean;
  profileMissing: boolean;
  presentationPolicy?: PrivateAgentJobsPresentationPolicy;
  selectedJobDetail: AgentResearchJobReadResponse | null;
  selectedJobDetailLoading: boolean;
  selectedJobId: string | null;
  showHeader?: boolean;
};

export function isAgentResearchJobTerminal(status: AgentResearchJobStatus) {
  return agentResearchJobIsTerminal(status);
}

/**
 * The Private Agent's work queue: what the owner's own computer is researching,
 * where it is stuck, and what came back for review. Presentation only, so every
 * state can be rendered directly in tests.
 */
export function PrivateAgentJobsPanelView({
  cancelingJobId,
  computerAvailability,
  enabled,
  emptyState,
  frame,
  jobs,
  jobsError,
  jobsLoading,
  materializeError,
  materializingJobId,
  onCancel,
  onMaterialize,
  onOpenComputers,
  onOpenPrivateAgentSettings,
  onSelectJob,
  profile,
  profileLoading,
  profileMissing,
  presentationPolicy = { showJobId: true },
  selectedJobDetail,
  selectedJobDetailLoading,
  selectedJobId,
  showHeader = true
}: PrivateAgentJobsPanelViewProps) {
  const { t } = useI18n();

  return (
    <div className="signals-panel" aria-label={t.privateAgentJobs.ariaLabel}>
      {showHeader ? (
        <header className="signals-header">
          <span className="signals-mark" aria-hidden="true">
            <AgentAvatar size="sm" variant="private" />
          </span>
          <div>
            <p className="eyebrow">{t.privateAgentJobs.eyebrow}</p>
            <h2>{profile ? profile.name : t.privateAgentJobs.title}</h2>
            <span style={headerIdentityStyle}>
              {!enabled ? null : computerAvailability === "missing" ? (
                <span>{t.privateAgentJobs.computer.missing}</span>
              ) : (
                <StatusDot
                  label={
                    computerAvailability === "online"
                      ? t.privateAgentJobs.computer.online
                      : computerAvailability === "offline"
                        ? t.privateAgentJobs.computer.offline
                        : t.privateAgentJobs.computer.revoked
                  }
                  size="sm"
                  status={computerAvailability}
                />
              )}
            </span>
          </div>
        </header>
      ) : null}

      {!enabled ? <p className="signals-empty">{t.privateAgentJobs.signIn}</p> : null}

      {enabled && profileLoading ? <Spinner label={t.privateAgentJobs.loading} size={16} /> : null}

      {enabled && !profileLoading && profileMissing ? (
        <EmptyState
          action={
            <Button onClick={onOpenPrivateAgentSettings} size="sm" variant="secondary">
              {t.privateAgentJobs.setUpAgent}
            </Button>
          }
          icon={<Bot size={20} />}
          title={t.privateAgentJobs.noAgentTitle}
        >
          {t.privateAgentJobs.noAgent}
        </EmptyState>
      ) : null}

      {enabled && !profileMissing ? (
        <>
          {computerAvailability === "revoked" || computerAvailability === "offline" ? (
            <Banner
              action={
                <Button onClick={onOpenComputers} size="sm" variant="secondary">
                  {t.privateAgentJobs.openComputers}
                </Button>
              }
              status="warning"
            >
              {computerAvailability === "revoked"
                ? t.privateAgentJobs.computerRevoked
                : t.privateAgentJobs.computerOffline}
            </Banner>
          ) : null}

          {jobsLoading ? <Spinner label={t.privateAgentJobs.loadingJobs} size={16} /> : null}
          {jobsError ? <Banner status="error">{t.privateAgentJobs.loadError}</Banner> : null}
          {materializeError ? <Banner status="error">{materializeErrorMessage(materializeError, t)}</Banner> : null}

          {!jobsLoading && !jobsError && jobs.length === 0 ? (
            <EmptyState icon={<Monitor size={20} />} title={emptyState?.title ?? t.privateAgentJobs.emptyTitle}>
              {emptyState?.description ?? t.privateAgentJobs.empty}
            </EmptyState>
          ) : null}

          {jobs.length > 0 ? (
            <div style={listStyle}>
              {jobs.map((job) => (
                <PrivateAgentJobRow
                  cancelingJobId={cancelingJobId}
                  computerAvailability={computerAvailability}
                  detail={selectedJobId === job.jobId ? selectedJobDetail : null}
                  detailLoading={selectedJobId === job.jobId && selectedJobDetailLoading}
                  expanded={selectedJobId === job.jobId}
                  frame={frame}
                  job={job}
                  key={job.jobId}
                  materializingJobId={materializingJobId}
                  onCancel={onCancel}
                  onMaterialize={onMaterialize}
                  onOpenComputers={onOpenComputers}
                  presentationPolicy={presentationPolicy}
                  onToggle={() => onSelectJob(selectedJobId === job.jobId ? null : job.jobId)}
                />
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function PrivateAgentJobRow({
  cancelingJobId,
  computerAvailability,
  detail,
  detailLoading,
  expanded,
  frame,
  job,
  materializingJobId,
  onCancel,
  onMaterialize,
  onOpenComputers,
  onToggle,
  presentationPolicy
}: {
  cancelingJobId: string | null;
  computerAvailability: PrivateAgentComputerAvailability;
  detail: AgentResearchJobReadResponse | null;
  detailLoading: boolean;
  expanded: boolean;
  frame: GraphVisualizationFrame | null;
  job: AgentResearchJob;
  materializingJobId: string | null;
  onCancel: (jobId: string) => void;
  onMaterialize: (jobId: string) => void;
  onOpenComputers: () => void;
  onToggle: () => void;
  presentationPolicy: PrivateAgentJobsPresentationPolicy;
}) {
  const { t } = useI18n();
  const statusDetail = agentResearchJobStatusDetail(job.status, computerAvailability, t);
  const cancellable = agentResearchJobIsCancellable(job.status);
  const stopReason = detail?.result?.stopReason;

  return (
    <Card padding="sm">
      <div style={rowHeadStyle}>
        <strong>
          {agentResearchTargetLabel(frame, job.targetCanonicalEntityId, presentationPolicy.targetFallbackLabel)}
        </strong>
        <Badge tone={agentResearchJobStatusTone(job.status)}>{agentResearchJobStatusLabel(job.status, t)}</Badge>
      </div>
      <p style={rowMetaStyle}>{t.privateAgentJobs.startedAt(compactDateTime(job.createdAt))}</p>
      {statusDetail ? <p style={rowMetaStyle}>{statusDetail}</p> : null}

      <div style={rowActionsStyle}>
        {job.status === "waiting_device" ? (
          <Button onClick={onOpenComputers} size="sm" variant="secondary">
            {t.privateAgentJobs.openComputers}
          </Button>
        ) : null}
        {job.status === "succeeded" ? (
          <Button
            disabled={materializingJobId === job.jobId}
            onClick={() => onMaterialize(job.jobId)}
            size="sm"
          >
            {materializingJobId === job.jobId ? t.privateAgentJobs.reviewPending : t.privateAgentJobs.review}
          </Button>
        ) : null}
        {cancellable ? (
          <Button
            disabled={cancelingJobId === job.jobId}
            onClick={() => onCancel(job.jobId)}
            size="sm"
            variant="ghost"
          >
            {cancelingJobId === job.jobId ? t.privateAgentJobs.canceling : t.privateAgentJobs.cancel}
          </Button>
        ) : null}
        <Button onClick={onToggle} size="sm" variant="ghost">
          {expanded ? t.privateAgentJobs.hideDetails : t.privateAgentJobs.showDetails}
        </Button>
      </div>

      {expanded ? (
        <div style={{ marginTop: "10px" }}>
          {detailLoading ? <Spinner label={t.privateAgentJobs.loadingDetails} size={14} /> : null}
          {/* `stopReason` only exists once the agent submitted a result. A job that
              failed before submitting one has no reason to show, and none is invented. */}
          {stopReason ? (
            <p style={rowMetaStyle}>{t.privateAgentJobs.stopReason(agentResearchStopReasonLabel(stopReason, t))}</p>
          ) : null}
          {detail && !detail.result && !detailLoading ? (
            <p style={rowMetaStyle}>{t.privateAgentJobs.noResultDetail}</p>
          ) : null}
          {presentationPolicy.showJobId ? <p style={rowMetaStyle}>{t.privateAgentJobs.jobId(job.jobId)}</p> : null}
        </div>
      ) : null}
    </Card>
  );
}

export type PrivateAgentJobsPanelProps = Omit<
  PrivateAgentJobsPanelViewProps,
  "onSelectJob" | "selectedJobDetail" | "selectedJobDetailLoading" | "selectedJobId"
>;

/** Owns the one extra read the list cannot answer: a single job's result. */
export function PrivateAgentJobsPanel(props: PrivateAgentJobsPanelProps) {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const detailQuery = useQuery({
    queryKey: ["agent-research-job", selectedJobId, props.profile?.agentId],
    queryFn: () => getAgentResearchJob(selectedJobId ?? ""),
    enabled: props.enabled && selectedJobId !== null,
    retry: false
  });

  return (
    <PrivateAgentJobsPanelView
      {...props}
      onSelectJob={setSelectedJobId}
      selectedJobDetail={detailQuery.data ?? null}
      selectedJobDetailLoading={selectedJobId !== null && detailQuery.isFetching}
      selectedJobId={selectedJobId}
    />
  );
}
